import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  getAdminMadeToMeasureEnquiries,
  resetMadeToMeasureAdminUpdate,
  resetMadeToMeasureAdminQuote,
  sendMadeToMeasureQuote,
  updateAdminMadeToMeasureEnquiry,
} from "../../actions/madeToMeasureActions";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import {
  selectMadeToMeasureAdminList,
  selectMadeToMeasureAdminQuote,
  selectMadeToMeasureAdminUpdate,
} from "../../selectors/madeToMeasureSelectors";
import { getSafeUserMessage } from "../../utils/errorMessages";

const STATUS_OPTIONS = [
  ["new", "New"],
  ["submitted", "Submitted"],
  ["reviewing", "Under review"],
  ["needs_details", "Needs more details"],
  ["quoted", "Quote prepared"],
  ["quote_sent", "Quote ready"],
  ["accepted", "Accepted"],
  ["payment_pending", "Payment pending"],
  ["paid", "Paid"],
  ["in_production", "In production"],
  ["cutting", "Cutting"],
  ["fitting", "Fitting"],
  ["finishing", "Finishing"],
  ["ready_for_collection", "Ready for collection"],
  ["dispatched", "Dispatched"],
  ["completed", "Completed"],
  ["rejected", "Declined / closed"],
  ["cancelled", "Cancelled"],
];

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function statusVariant(status = "") {
  switch (status) {
    case "paid":
    case "in_production":
    case "cutting":
    case "fitting":
    case "finishing":
    case "ready_for_collection":
    case "dispatched":
    case "completed":
      return "success";
    case "payment_pending":
    case "accepted":
      return "info";
    case "quote_sent":
    case "quoted":
      return "primary";
    case "needs_details":
      return "danger";
    case "rejected":
    case "cancelled":
      return "secondary";
    default:
      return "warning";
  }
}

function formatStatus(status = "") {
  return (
    STATUS_OPTIONS.find(([value]) => value === status)?.[1] ||
    safeText(status).replace(/_/g, " ") ||
    "Unknown"
  );
}

function measurementSummary(measurements = {}) {
  if (measurements.method === "appointment") return "In-store appointment";

  const unit = measurements.unit || "cm";
  return ["waist", "hip", "bust", "length", "shoulder", "sleeve"]
    .map((key) => {
      const value = safeText(measurements[key]);
      return value ? `${key}: ${value} ${unit}` : "";
    })
    .filter(Boolean)
    .join(", ");
}

function getProductName(item) {
  return item?.name || item?.productName || "Not selected";
}

function getSnapshotName(snapshot, fallback = "Not selected") {
  return safeText(snapshot?.name) || fallback;
}

function getSelectionName(enquiry = {}, selectionKey = "", snapshotKey = "") {
  return (
    getSnapshotName(enquiry[snapshotKey], "") ||
    getProductName(enquiry[selectionKey])
  );
}

function getHaberdasherySummary(enquiry = {}) {
  const snapshots = Array.isArray(enquiry.haberdasherySnapshots)
    ? enquiry.haberdasherySnapshots
    : [];

  if (snapshots.length) {
    return snapshots
      .map((item) => {
        const name = getSnapshotName(item.snapshot, "");
        return name ? `${name} x ${Number(item.quantity || 1)}` : "";
      })
      .filter(Boolean)
      .join(", ");
  }

  return enquiry.selectedHaberdashery?.length
    ? enquiry.selectedHaberdashery.map((item) => item.name).join(", ")
    : "No finishing selected";
}

function fulfillmentSummary(fulfillment = {}) {
  const method = safeText(fulfillment.method || "pickup");

  if (method === "delivery") {
    const address = fulfillment.deliveryAddress || {};
    return [
      "Delivery",
      address.line1,
      address.city,
      address.postalCode,
      address.country,
    ]
      .filter((value) => safeText(value))
      .join(", ");
  }

  return ["Collection", fulfillment.storeKey, fulfillment.pickupNotes]
    .filter((value) => safeText(value))
    .join(", ");
}

function formatDateTime(value = "") {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString();
}

function TimelineList({ items = [], empty = "No activity yet." }) {
  const safeItems = Array.isArray(items) ? items : [];

  if (!safeItems.length) {
    return <div className="text-muted small">{empty}</div>;
  }

  return (
    <div className="d-grid gap-2">
      {safeItems.slice(-6).reverse().map((item) => {
        const label = item.status || item.type || item.senderType || "message";

        return (
          <div
            key={item.id || item.createdAt || item.message}
            className="border rounded p-2 bg-light"
          >
            <div className="d-flex justify-content-between gap-2">
              <strong className="small">{formatStatus(label)}</strong>
              <span className="text-muted small">{formatDateTime(item.createdAt)}</span>
            </div>
            <div className="small">{item.message || "-"}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminMadeToMeasureScreen() {
  const dispatch = useDispatch();
  const listState = useSelector(selectMadeToMeasureAdminList);
  const updateState = useSelector(selectMadeToMeasureAdminUpdate);
  const quoteState = useSelector(selectMadeToMeasureAdminQuote);

  const [activeId, setActiveId] = useState("");
  const [statusForm, setStatusForm] = useState({
    status: "new",
    quoteMessage: "",
    adminNotes: "",
    estimatedExtraCost: "",
    assignedStoreKey: "",
  });
  const [quoteForm, setQuoteForm] = useState({
    finalQuotePrice: "",
    quoteMessage: "",
    expiresInDays: "14",
  });

  useEffect(() => {
    dispatch(getAdminMadeToMeasureEnquiries()).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load made-to-measure enquiries right now. Please try again later.",
        ),
      );
    });

    return () => {
      dispatch(resetMadeToMeasureAdminUpdate());
      dispatch(resetMadeToMeasureAdminQuote());
    };
  }, [dispatch]);

  const enquiries = useMemo(
    () => (Array.isArray(listState.enquiries) ? listState.enquiries : []),
    [listState.enquiries],
  );
  const activeEnquiry = useMemo(
    () => enquiries.find((item) => item.id === activeId || item._id === activeId),
    [activeId, enquiries],
  );

  useEffect(() => {
    if (!activeId && enquiries[0]) {
      setActiveId(enquiries[0].id || enquiries[0]._id);
    }
  }, [activeId, enquiries]);

  useEffect(() => {
    if (!activeEnquiry) return;

    setStatusForm({
      status: activeEnquiry.status || "new",
      quoteMessage: activeEnquiry.quoteMessage || "",
      adminNotes: activeEnquiry.adminNotes || "",
      assignedStoreKey: activeEnquiry.assignedStoreKey || "",
      estimatedExtraCost:
        activeEnquiry.estimatedExtraCost || activeEnquiry.estimatedExtraCost === 0
          ? String(activeEnquiry.estimatedExtraCost)
          : "",
    });

    setQuoteForm({
      finalQuotePrice: activeEnquiry.finalQuotePrice
        ? String(activeEnquiry.finalQuotePrice)
        : "",
      quoteMessage: activeEnquiry.quoteMessage || "",
      expiresInDays: "14",
    });
  }, [activeEnquiry]);

  const updateQuoteForm = (key, value) => {
    setQuoteForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const updateStatusForm = (key, value) => {
    setStatusForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSaveUpdate = async (event) => {
    event.preventDefault();

    if (!activeEnquiry) return;

    try {
      const payload = {
        status: statusForm.status,
        quoteMessage: statusForm.quoteMessage,
        adminNotes: statusForm.adminNotes,
        assignedStoreKey: statusForm.assignedStoreKey,
        generateCustomerUpdateLink: true,
      };

      if (safeText(statusForm.estimatedExtraCost)) {
        payload.estimatedExtraCost = statusForm.estimatedExtraCost;
      }

      const result = await dispatch(
        updateAdminMadeToMeasureEnquiry(
          activeEnquiry.id || activeEnquiry._id,
          payload,
        ),
      );

      toast.success(result?.message || "Customer update published.");
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not publish this update right now. Please try again later.",
        ),
      );
    }
  };

  const handleSendQuote = async (event) => {
    event.preventDefault();

    if (!activeEnquiry) return;

    try {
      const result = await dispatch(
        sendMadeToMeasureQuote(activeEnquiry.id || activeEnquiry._id, {
          finalQuotePrice: quoteForm.finalQuotePrice,
          quoteMessage: quoteForm.quoteMessage,
          expiresInDays: quoteForm.expiresInDays,
          notifyByEmail: false,
        }),
      );

      toast.success(result?.message || "Quote published to the customer screen.");

      if (result?.quoteUrl && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.quoteUrl);
      }
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not prepare this quote right now. Please try again later.",
        ),
      );
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-start g-3 mb-4">
        <Col>
          <h1>Made To Measure Enquiries</h1>
          <div className="text-muted">
            Review customer builder submissions, publish final quotes to the
            customer update screen, and track quote acceptance.
          </div>
        </Col>

        <Col md="auto">
          <Link to="/made-to-measure" className="btn btn-outline-dark">
            Customer page
          </Link>
        </Col>
      </Row>

      {listState.error ? (
        <Message variant="danger">{listState.error}</Message>
      ) : null}
      {updateState.error ? (
        <Message variant="danger">{updateState.error}</Message>
      ) : null}
      {quoteState.error ? (
        <Message variant="danger">{quoteState.error}</Message>
      ) : null}
      {listState.loading ? <Loader /> : null}

      <Row className="g-4">
        <Col lg={5}>
          <div className="d-grid gap-3">
            {enquiries.map((enquiry) => {
              const id = enquiry.id || enquiry._id;
              const isActive = id === activeId;

              return (
                <button
                  type="button"
                  key={id}
                  className={`text-start border rounded bg-white p-3 ${
                    isActive ? "border-dark shadow-sm" : ""
                  }`}
                  onClick={() => setActiveId(id)}
                >
                  <div className="d-flex justify-content-between gap-2">
                    <strong>{enquiry.customerName}</strong>
                    <Badge bg={statusVariant(enquiry.status)}>
                      {formatStatus(enquiry.status)}
                    </Badge>
                  </div>
                  <div className="text-muted small">{enquiry.customerEmail}</div>
                  <div className="mt-2">
                    {getSelectionName(enquiry, "selectedProduct", "productSnapshot")} in{" "}
                    {getSelectionName(enquiry, "selectedTextile", "textileSnapshot")}
                  </div>
                  {enquiry.assignedStoreKey ? (
                    <div className="text-muted small mt-1">
                      Store: {enquiry.assignedStoreKey}
                    </div>
                  ) : null}
                  <div className="text-muted small mt-1">
                    Submitted {formatDateTime(enquiry.createdAt)}
                  </div>
                </button>
              );
            })}

            {!listState.loading && !enquiries.length ? (
              <Message>No made to measure enquiries yet.</Message>
            ) : null}
          </div>
        </Col>

        <Col lg={7}>
          {activeEnquiry ? (
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between gap-3 flex-wrap">
                  <div>
                    <h2 className="h4 mb-1">{activeEnquiry.customerName}</h2>
                    <div>
                      <a href={`mailto:${activeEnquiry.customerEmail}`}>
                        {activeEnquiry.customerEmail}
                      </a>
                    </div>
                    {activeEnquiry.customerPhone ? (
                      <div>
                        <a href={`tel:${activeEnquiry.customerPhone}`}>
                          {activeEnquiry.customerPhone}
                        </a>
                      </div>
                    ) : null}
                  </div>

                  <Badge
                    bg={statusVariant(activeEnquiry.status)}
                    className="align-self-start"
                  >
                    {formatStatus(activeEnquiry.status)}
                  </Badge>
                </div>

                <hr />

                <Row className="g-3">
                  <Col md={6}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Design
                    </div>
                    <div>
                      {getSelectionName(
                        activeEnquiry,
                        "selectedProduct",
                        "productSnapshot",
                      )}
                    </div>
                    {activeEnquiry.productSnapshot?.sku ? (
                      <div className="text-muted small">
                        SKU {activeEnquiry.productSnapshot.sku}
                      </div>
                    ) : null}
                    <div className="text-muted small">
                      Estimate: {formatMoney(activeEnquiry.estimatedBasePrice)}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Textile
                    </div>
                    <div>
                      {getSelectionName(
                        activeEnquiry,
                        "selectedTextile",
                        "textileSnapshot",
                      )}
                    </div>
                    {activeEnquiry.textileSnapshot?.material ? (
                      <div className="text-muted small">
                        {activeEnquiry.textileSnapshot.material}
                      </div>
                    ) : null}
                    <div className="text-muted small">
                      Extra estimate:{" "}
                      {formatMoney(activeEnquiry.estimatedExtraCost)}
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Trims
                    </div>
                    <div>{getHaberdasherySummary(activeEnquiry)}</div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Measurements
                    </div>
                    <div>
                      {measurementSummary(activeEnquiry.measurements) ||
                        "No measurements supplied"}
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Fulfillment
                    </div>
                    <div>
                      {fulfillmentSummary(activeEnquiry.fulfillment) ||
                        "No delivery preference saved"}
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Customer notes
                    </div>
                    <div>{activeEnquiry.notes || "-"}</div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold">
                      Customer response
                    </div>
                    <div>
                      {activeEnquiry.quoteAcceptedAt
                        ? `Accepted ${new Date(
                            activeEnquiry.quoteAcceptedAt,
                          ).toLocaleString()}`
                        : activeEnquiry.quoteDeclinedAt
                          ? `Declined ${new Date(
                              activeEnquiry.quoteDeclinedAt,
                            ).toLocaleString()}`
                          : "No quote response yet"}
                    </div>
                    {activeEnquiry.customerResponseMessage ? (
                      <div className="text-muted small mt-1">
                        {activeEnquiry.customerResponseMessage}
                      </div>
                    ) : null}
                  </Col>

                  <Col md={6}>
                    <div className="text-muted small text-uppercase fw-bold mb-2">
                      Customer-visible messages
                    </div>
                    <TimelineList
                      items={activeEnquiry.messages}
                      empty="No customer messages yet."
                    />
                  </Col>

                  <Col md={6}>
                    <div className="text-muted small text-uppercase fw-bold mb-2">
                      Timeline
                    </div>
                    <TimelineList items={activeEnquiry.timeline} />
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted small text-uppercase fw-bold mb-2">
                      Quote versions
                    </div>
                    {activeEnquiry.quoteVersions?.length ? (
                      <div className="d-grid gap-2">
                        {activeEnquiry.quoteVersions.map((version) => (
                          <div
                            key={version.id || version.version}
                            className="border rounded p-2 bg-light"
                          >
                            <div className="d-flex justify-content-between gap-2">
                              <strong>Version {version.version}</strong>
                              <span>{formatMoney(version.finalQuotePrice)}</span>
                            </div>
                            <div className="text-muted small">
                              Sent {formatDateTime(version.sentAt)}
                              {version.expiresAt
                                ? ` | Expires ${formatDateTime(version.expiresAt)}`
                                : ""}
                            </div>
                            {version.quoteMessage ? (
                              <div className="small mt-1">{version.quoteMessage}</div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small">No quote versions yet.</div>
                    )}
                  </Col>
                </Row>

                <hr />

                <Form onSubmit={handleSaveUpdate}>
                  <h3 className="h5 mb-3">Publish customer update</h3>
                  <Row className="g-3">
                    <Col md={5}>
                      <Form.Group controlId="mtmStatus">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={statusForm.status}
                          onChange={(event) =>
                            updateStatusForm("status", event.target.value)
                          }
                        >
                          {STATUS_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="mtmEstimatedExtra">
                        <Form.Label>Estimated extra</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>£</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={statusForm.estimatedExtraCost}
                            onChange={(event) =>
                              updateStatusForm(
                                "estimatedExtraCost",
                                event.target.value,
                              )
                            }
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId="mtmAssignedStore">
                        <Form.Label>Store key</Form.Label>
                        <Form.Control
                          type="text"
                          value={statusForm.assignedStoreKey}
                          onChange={(event) =>
                            updateStatusForm(
                              "assignedStoreKey",
                              event.target.value,
                            )
                          }
                          placeholder="london"
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group controlId="mtmCustomerUpdate">
                        <Form.Label>Customer-facing update</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={statusForm.quoteMessage}
                          onChange={(event) =>
                            updateStatusForm("quoteMessage", event.target.value)
                          }
                          placeholder="Tell the customer what has changed, what you need, or what happens next."
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group controlId="mtmAdminNotes">
                        <Form.Label>Internal admin notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={statusForm.adminNotes}
                          onChange={(event) =>
                            updateStatusForm("adminNotes", event.target.value)
                          }
                          placeholder="Private notes for the team. Customers cannot see this."
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 flex-wrap align-items-center mt-3">
                    <Button
                      type="submit"
                      variant="outline-dark"
                      disabled={updateState.loading}
                    >
                      {updateState.loading ? "Publishing update..." : "Publish update"}
                    </Button>

                    {activeEnquiry.linkedOrder?.orderNumber ? (
                      <Link
                        to={`/admin/orders/${activeEnquiry.linkedOrder.orderNumber}`}
                        className="btn btn-outline-dark"
                      >
                        View linked order
                      </Link>
                    ) : null}
                  </div>
                </Form>

                {updateState.customerUpdateUrl ? (
                  <div className="mt-4">
                    <Form.Label>Customer update screen</Form.Label>
                    <InputGroup>
                      <Form.Control readOnly value={updateState.customerUpdateUrl} />
                      <Button
                        variant="outline-dark"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            updateState.customerUpdateUrl,
                          );
                          toast.info("Customer update link copied.");
                        }}
                      >
                        Copy
                      </Button>
                    </InputGroup>
                    <div className="text-muted small mt-2">
                      Share this if the customer needs another secure link to
                      their update screen.
                    </div>
                  </div>
                ) : null}

                <hr />

                <Form onSubmit={handleSendQuote}>
                  <h3 className="h5 mb-3">Final quote</h3>
                  <Row className="g-3">
                    <Col md={5}>
                      <Form.Group controlId="mtmFinalQuote">
                        <Form.Label>Final quote price</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>£</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={quoteForm.finalQuotePrice}
                            onChange={(event) =>
                              updateQuoteForm("finalQuotePrice", event.target.value)
                            }
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId="mtmQuoteExpiry">
                        <Form.Label>Quote valid for</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="90"
                          value={quoteForm.expiresInDays}
                          onChange={(event) =>
                            updateQuoteForm("expiresInDays", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group controlId="mtmQuoteMessage">
                        <Form.Label>Message to customer</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={quoteForm.quoteMessage}
                          onChange={(event) =>
                            updateQuoteForm("quoteMessage", event.target.value)
                          }
                          placeholder="Add fit, fabric, production, or timing notes for the final quote."
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 flex-wrap align-items-center mt-3">
                    <Button
                      type="submit"
                      variant="dark"
                      disabled={quoteState.loading}
                    >
                      {quoteState.loading ? "Publishing quote..." : "Publish final quote"}
                    </Button>
                  </div>
                </Form>

                {quoteState.customerUpdateUrl ? (
                  <div className="mt-4">
                    <Form.Label>Customer update screen</Form.Label>
                    <InputGroup>
                      <Form.Control readOnly value={quoteState.customerUpdateUrl} />
                      <Button
                        variant="outline-dark"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            quoteState.customerUpdateUrl,
                          );
                          toast.info("Customer update link copied.");
                        }}
                      >
                        Copy
                      </Button>
                    </InputGroup>
                    <div className="text-muted small mt-2">
                      Share this if the customer needs another secure link to
                      their update screen. Existing customer links still work.
                    </div>
                  </div>
                ) : null}

                {quoteState.quoteUrl ? (
                  <div className="mt-4">
                    <Form.Label>Direct quote payment link</Form.Label>
                    <InputGroup>
                      <Form.Control readOnly value={quoteState.quoteUrl} />
                      <Button
                        variant="outline-dark"
                        onClick={() => {
                          navigator.clipboard?.writeText(quoteState.quoteUrl);
                          toast.info("Quote link copied.");
                        }}
                      >
                        Copy
                      </Button>
                    </InputGroup>
                    <div className="text-muted small mt-2">
                      The customer can pay from their enquiry updates screen.
                      This direct quote link is available as a fallback.
                    </div>
                  </div>
                ) : null}
              </Card.Body>
            </Card>
          ) : (
            <Message>Select an enquiry to review.</Message>
          )}
        </Col>
      </Row>
    </Container>
  );
}
